/* WebSocket — connection management and send helper */

import { state, sessionLastSeq } from './state.js';
import {
  getAuthMode, getAccessToken, getActiveConnection, getActiveConversation, refreshAccessToken,
  authHeader,
} from './auth.js';

export let ws = null;
let wsReconnectTimer = null;
let _onMessage = null;

// Reconnect state, advanced ONLY through the pure retryState* helpers below so the ladder's
// behavior (attempt growth, refresh limits, terminal auth-death) is unit-testable without a
// DOM or a real socket (see ws-backoff.test.mjs). `attempt` counts consecutive unhealthy
// closes; the per-attempt delay is computed by the pure reconnectDelay() — exponential
// base*2^n, capped, with FULL jitter. Only a socket that PROVES itself (an inbound frame
// arrives) resets the ladder: a mere onopen doesn't, because the server may complete the
// handshake just to deliver a readable close code (accept-then-close rejections).
let _retry = initialRetryState();
let _openedThisSocket = false;   // onopen fired (a rejected handshake never opens)
let _sawFrameThisSocket = false; // an inbound frame proves connection + auth work end-to-end
const RECONNECT_BASE_MS = 1000;
const RECONNECT_CAP_MS = 30000;
// This many consecutive token-refresh failures on auth closes = the session is dead for good:
// stop reconnecting and hand the UI a distinct frame so it can offer a fresh sign-in.
const MAX_REFRESH_FAILURES = 3;
// A rejected handshake is unreadable in the browser (generic close, no HTTP status). After
// this many consecutive never-opened closes, one cheap authenticated GET splits "signed out"
// (stop + re-auth) from "server down" (keep the capped backoff).
const PROBE_AFTER_UNOPENED = 3;

// Heartbeat: both sockets answer {"type":"ping"} with {"type":"pong"} — the local /ws/chat and the
// hosted run socket (whose reader handles ping as a control frame, never a user turn). We send a
// periodic ping and force-close the socket if NO traffic arrives within a stale window, so a
// half-open socket (e.g. laptop sleep) is detected and reconnects via the same backoff instead of
// silently dying. On the hosted socket that reconnect also carries the resume frame (see onopen),
// so noticing the drop quickly is what lets a still-streaming run be re-attached in time.
let heartbeatTimer = null;
let lastActivityAt = 0;
const HEARTBEAT_INTERVAL_MS = 25000;
const HEARTBEAT_STALE_MS = 60000; // ~2 missed pings with no reply -> treat the socket as dead

export function setWsHandler(handler) {
  _onMessage = handler;
}

// Pure builder for the hosted re-attach frame sent on socket open. last_seq is the conversation's
// replay cursor (the session's seq high-water mark): the server replays only events with
// seq > last_seq of a still-streaming run, then attaches this socket to the live tail. 0 after a
// full reload (fresh state) replays the whole run; anything non-numeric/negative normalizes to 0.
// Exported for the pure unit test (see ws-resume.test.mjs).
export function resumeFrame(lastSeq) {
  const n = Number(lastSeq);
  return { type: "resume", last_seq: Number.isFinite(n) && n > 0 ? Math.floor(n) : 0 };
}

// Pure backoff delay with FULL jitter: a uniformly random point in [0, min(cap, base*2^attempt)].
// `attempt` starts at 0 (first retry is small, bounded by base); negative attempts clamp to 0.
// Exported so the ceiling/cap/jitter invariants are unit-testable (see ws-backoff.test.mjs).
export function reconnectDelay(attempt, { base = RECONNECT_BASE_MS, cap = RECONNECT_CAP_MS } = {}) {
  const ceiling = Math.min(cap, base * 2 ** Math.max(0, attempt));
  return Math.random() * ceiling;
}

// ---------------------------------------------------------------------------
// Pure reconnect policy — exported for unit tests (ws-backoff.test.mjs).
// ---------------------------------------------------------------------------

export function initialRetryState() {
  return { attempt: 0, refreshFailures: 0, unopenedCloses: 0, authCloses: 0, terminal: false };
}

// An inbound frame arrived on the open socket: the connection AND the auth provably work
// end-to-end, so every ladder resets (the next incident starts a fresh backoff).
export function retryStateOnFrame(s) {
  return { ...s, attempt: 0, refreshFailures: 0, unopenedCloses: 0, authCloses: 0 };
}

// Classify a close and advance the counters. `opened` = onopen fired for this socket (a
// rejected handshake never opens, and the browser reports only a generic code for it).
// Actions:
//   "none"     — already terminal: the loop stays stopped.
//   "terminal" — forbidden (4403): re-auth can't fix this socket; stop for good.
//   "refresh"  — auth close (4401): mint a fresh token, then reconnect/back off/stop
//                (see retryStateOnRefresh).
//   "probe"    — too many unreadable handshake failures in a row: ask an authed HTTP
//                endpoint whether the session is alive before retrying blindly.
//   "retry"    — anything else (server down, network blip): capped-backoff reconnect.
export function retryStateOnClose(s, { code, opened, hosted },
                                  { probeAfter = PROBE_AFTER_UNOPENED } = {}) {
  if (s.terminal) return { state: s, action: "none" };
  const next = {
    ...s,
    attempt: s.attempt + 1, // every unhealthy close grows the ladder — opened-then-closed too
    unopenedCloses: opened ? 0 : s.unopenedCloses + 1,
    authCloses: hosted && code === 4401 ? s.authCloses + 1 : 0,
  };
  if (hosted && code === 4403) return { state: { ...next, terminal: true }, action: "terminal" };
  if (hosted && code === 4401) return { state: next, action: "refresh" };
  if (hosted && !opened && next.unopenedCloses >= probeAfter) return { state: next, action: "probe" };
  return { state: next, action: "retry" };
}

// Outcome of a token refresh triggered by an auth close (or by the probe). Success on the
// FIRST auth close in a row reconnects immediately (a routine token expiry); repeated auth
// closes without a healthy frame in between mean the fresh token is being rejected too, so
// back off instead of tight-looping. N consecutive refresh FAILURES mean the session is
// unrecoverable (the refresh cookie is dead): go terminal.
export function retryStateOnRefresh(s, ok, { maxRefreshFailures = MAX_REFRESH_FAILURES } = {}) {
  if (ok) {
    return { state: { ...s, refreshFailures: 0 }, action: s.authCloses <= 1 ? "connect" : "retry" };
  }
  const refreshFailures = s.refreshFailures + 1;
  if (refreshFailures >= maxRefreshFailures) {
    return { state: { ...s, refreshFailures, terminal: true }, action: "terminal" };
  }
  return { state: { ...s, refreshFailures }, action: "retry" };
}

// ---------------------------------------------------------------------------
// Impure loop
// ---------------------------------------------------------------------------

// Schedule one reconnect after the jittered backoff for the current ladder position (the
// Nth consecutive unhealthy close retries with delay(N-1), so the first retry is small).
// Guarded against stacking timers and against a stopped (terminal) loop. Reusing
// wsReconnectTimer keeps reconnectWs()'s clear-to-cancel invariant intact.
function _scheduleReconnect() {
  if (wsReconnectTimer || _retry.terminal) return;
  const delay = reconnectDelay(Math.max(0, _retry.attempt - 1));
  wsReconnectTimer = setTimeout(() => { wsReconnectTimer = null; connectWs(); }, delay);
}

// The session is dead for good (forbidden, or refresh keeps failing): stop the reconnect
// loop and hand the UI a distinct frame so it can surface a persistent sign-in affordance
// (a toast would be wrong — nothing will recover on its own).
function _goTerminal() {
  _retry = { ..._retry, terminal: true };
  if (wsReconnectTimer) { clearTimeout(wsReconnectTimer); wsReconnectTimer = null; }
  _stopHeartbeat();
  _onMessage?.({ type: "_ws_auth_dead" });
}

// Auth close / probe said the token is dead: mint a fresh one, then let the pure policy
// decide — reconnect now, back off, or stop for good after repeated failures. A refresh
// that failed while the server is UNREACHABLE doesn't count a strike toward terminal:
// that's the plain server-down case (refreshAccessToken() can't tell a network error from
// a rejected cookie — it returns false for both), and an outage longer than the retry
// ladder must never flip into a false "signed out".
function _refreshThenReconnect() {
  refreshAccessToken().then(async (ok) => {
    if (!ok && !(await _serverReachable())) {
      _scheduleReconnect();
      return;
    }
    const { state: next, action } = retryStateOnRefresh(_retry, ok);
    _retry = next;
    if (action === "terminal") _goTerminal();
    else if (action === "connect") connectWs();
    else _scheduleReconnect();
  });
}

// Cheap unauthenticated liveness check against the always-open health endpoint (the same
// one auth.js probes at boot). False on network failure OR a 5xx — neither is an auth problem.
async function _serverReachable() {
  try {
    return (await fetch("/health", { headers: { Accept: "application/json" } })).ok;
  } catch {
    return false;
  }
}

// After several never-opened closes (a rejected handshake is unreadable in the browser),
// one cheap authenticated GET — the same /api/me auth.js confirms sessions with — splits
// the cases: 401 -> the token is dead, run the refresh path (repeated failure = signed out
// for good); anything else (200, 5xx, network error) -> auth is not the problem, keep the
// capped backoff exactly as for a plain server-down.
async function _probeAuthThenReconnect() {
  let status = null;
  try {
    const r = await fetch("/api/me", { headers: { ...authHeader(), Accept: "application/json" } });
    status = r.status;
  } catch { /* network down — plain server-down; keep retrying */ }
  if (status === 401) { _refreshThenReconnect(); return; }
  _scheduleReconnect();
}

// Build the chat socket URL for the current mode. Local/desktop uses the single-operator /ws/chat;
// hosted uses the per-connection run socket and passes the access token via a WS subprotocol (see
// connectWs) rather than the URL. Returns null in hosted mode until a connection + conversation exist.
function _wsUrl() {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  if (getAuthMode() === "hosted") {
    const cid = getActiveConnection();
    const coid = getActiveConversation();
    const tok = getAccessToken();
    if (!cid || !coid || !tok) return null;
    // Token travels in the Sec-WebSocket-Protocol subprotocol (see connectWs), NOT the URL — so it
    // never lands in access logs / proxies / history. The URL carries only the ids.
    return `${proto}//${location.host}/ws/connections/${encodeURIComponent(cid)}`
      + `/conversations/${encodeURIComponent(coid)}`;
  }
  return `${proto}//${location.host}/ws/chat`;
}

export function connectWs() {
  if (_retry.terminal) return; // signed out for good — only a fresh sign-in restarts the loop
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  const url = _wsUrl();
  if (!url) {
    // Hosted with a bound connection+conversation but NO access token: a failed refresh
    // cleared it (auth.js), so a socket can't even be attempted. Run the refresh path
    // instead of silently stopping — it either mints a token and reconnects, or its
    // consecutive-failure limit goes terminal and surfaces the sign-in banner. Without
    // this the loop would end here with no retry, no terminal state, and no UI signal.
    if (getAuthMode() === "hosted" && getActiveConnection() && getActiveConversation()
        && !getAccessToken()) {
      _refreshThenReconnect();
    }
    return; // otherwise: nothing to connect to yet (no connection/conversation)
  }
  _openedThisSocket = false;
  _sawFrameThisSocket = false;
  if (getAuthMode() === "hosted") {
    // Offer the version tag + the token-bearing subprotocol; the server echoes "cpq.v1".
    ws = new WebSocket(url, ["cpq.v1", "cpq.token." + getAccessToken()]);
  } else {
    ws = new WebSocket(url);
  }

  ws.onopen = () => {
    // NOTE: an open alone does NOT reset the backoff ladder — the server completes the
    // handshake even to reject (accept-then-close, so the close code is readable). The
    // ladder resets on the first inbound frame instead (see onmessage).
    _openedThisSocket = true;
    state.wsConnected = true;
    _startHeartbeat();
    if (getAuthMode() === "hosted") {
      // Re-attach to a still-streaming run for this conversation (the run socket URL is
      // per-conversation, so the cursor must come from that conversation's session, not
      // whatever the UI considers active). The server replies agent_resumed + a replay of
      // the missed tail, or agent_no_active_run. Replayed frames the client already applied
      // are dropped by the seq gate, so overlap is harmless. Every (re)connect flows through
      // here — backoff reconnects AND conversation switches via reconnectWs() — so each
      // socket presents its own conversation's cursor. Local /ws/chat has no resume protocol.
      wsSend(resumeFrame(sessionLastSeq(getActiveConversation())));
    }
    _onMessage?.({ type: "_ws_status" });
  };

  ws.onmessage = (ev) => {
    lastActivityAt = Date.now(); // any inbound frame (incl. our pong) proves the socket is alive
    if (!_sawFrameThisSocket) {
      _sawFrameThisSocket = true;
      _retry = retryStateOnFrame(_retry); // proven connection + auth: fresh ladder
    }
    try {
      const data = JSON.parse(ev.data);
      _onMessage?.(data);
    } catch (e) {
      console.error("WS parse error", e);
    }
  };

  ws.onclose = (ev) => {
    state.wsConnected = false;
    _stopHeartbeat();
    _onMessage?.({ type: "_ws_status" });
    // The pure policy classifies the close: 4401 (token expired/invalid — the server accepts
    // then closes so this code is readable) runs the refresh path; 4403 (forbidden) and
    // repeated refresh failures stop the loop for good; repeated never-opened closes (an
    // unreadable rejected handshake) trigger one authenticated probe; everything else —
    // plain server-down included — retries on the capped, jittered backoff.
    const { state: next, action } = retryStateOnClose(_retry, {
      code: ev?.code, opened: _openedThisSocket, hosted: getAuthMode() === "hosted",
    });
    _retry = next;
    if (action === "none") return;
    if (action === "terminal") { _goTerminal(); return; }
    if (action === "refresh") { _refreshThenReconnect(); return; }
    if (action === "probe") { _probeAuthThenReconnect(); return; }
    _scheduleReconnect();
  };

  ws.onerror = () => {};
}

// Heartbeat runs in every mode: local /ws/chat has always answered ping, and the hosted run
// socket's reader now answers {"type":"ping"} with {"type":"pong"} as a control frame (never a
// user turn), so hosted half-open sockets are stale-closed and re-attach via resume too. Kept as
// a seam so a future transport that can't answer ping can opt out.
function _heartbeatEnabled() {
  return true;
}

function _startHeartbeat() {
  _stopHeartbeat();
  if (!_heartbeatEnabled()) return;
  lastActivityAt = Date.now();
  heartbeatTimer = setInterval(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    // No traffic (not even a pong) within the stale window -> the socket is half-open; force-close
    // so onclose runs the backoff reconnect. onclose is still wired, so this reconnects cleanly.
    if (Date.now() - lastActivityAt > HEARTBEAT_STALE_MS) {
      try { ws.close(); } catch { /* already closing */ }
      return;
    }
    try { ws.send(JSON.stringify({ type: "ping" })); } catch { /* send failed — onclose will fire */ }
  }, HEARTBEAT_INTERVAL_MS);
}

function _stopHeartbeat() {
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
}

// Rebind the socket to the current connection/conversation (used when switching conversations):
// drop the old socket without triggering its backoff-reconnect, then connect fresh to the new URL.
export function reconnectWs() {
  if (wsReconnectTimer) { clearTimeout(wsReconnectTimer); wsReconnectTimer = null; }
  // Deliberate rebind to a known-good target — start the ladder fresh. A terminal (auth-dead)
  // state survives the rebind: switching conversations can't resurrect a dead session.
  _retry = { ...initialRetryState(), terminal: _retry.terminal };
  _stopHeartbeat();
  if (ws) {
    try { ws.onclose = null; ws.close(); } catch { /* already closing */ }
    ws = null;
  }
  connectWs();
}

export function wsSend(msg) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(msg));
  }
}
